import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import CampaignsTable from "@/components/campaigns/CampaignsTable";
import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import SearchInput from "@/components/common/SearchInput";
import { usePageTitle } from "@/components/common/AppShell";
import { Plus } from "lucide-react";

export default function CampaignsPage() {
  usePageTitle("Campaigns");
  const navigate = useNavigate();
  const {
    campaigns,
    campaignsLoading,
    campaignsError,
    refreshCampaigns,
    deleteCampaign,
    currentUserType,
    users,
    segments,
    getCampaignStatistics,
  } = useApp();
  const [campaignStats, setCampaignStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(new Set());
  const requestedStatsRef = useRef(new Set());
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const tabCounts = useMemo(() => {
    const counts = { all: campaigns.length, active: 0, completed: 0, draft: 0 };
    for (const c of campaigns) {
      if (c.status in counts) counts[c.status] += 1;
    }
    return counts;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (activeTab !== "all" && c.status !== activeTab) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q) ||
        c.createdBy?.toLowerCase().includes(q)
      );
    });
  }, [campaigns, search, activeTab]);

  const STATUS_TABS = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "completed", label: "Completed" },
    { id: "draft", label: "Draft" },
  ];

  const handleCreate = () => {
    navigate("/dashboard/campaigns/new");
  };

  const handleEdit = (campaign) => {
    navigate(`/dashboard/campaigns/${campaign.uuid}/edit`);
  };

  const handleDelete = async (campaign) => {
    const confirmed =
      typeof window !== "undefined"
        ? window.confirm(`Delete campaign "${campaign.name}"?`)
        : true;

    if (!confirmed) return;

    try {
      await deleteCampaign(campaign.uuid);
    } catch (error) {
      console.error("Failed to delete campaign", error);
      if (typeof window !== "undefined") {
        window.alert("Failed to delete campaign. Please try again.");
      }
    }
  };

  const loadCampaignStatistics = useCallback(
    async (campaignUuid) => {
      if (
        !campaignUuid ||
        loadingStats.has(campaignUuid) ||
        requestedStatsRef.current.has(campaignUuid)
      ) {
        return;
      }

      requestedStatsRef.current.add(campaignUuid);
      setLoadingStats((prev) => new Set(prev).add(campaignUuid));
      try {
        const stats = await getCampaignStatistics(campaignUuid);
        setCampaignStats((prev) => ({ ...prev, [campaignUuid]: stats }));
      } catch (error) {
        console.error(
          `Failed to load statistics for campaign ${campaignUuid}`,
          error,
        );
        setCampaignStats((prev) => ({
          ...prev,
          [campaignUuid]: { totalReceivers: 0, uniqueReceivers: 0 },
        }));
      } finally {
        setLoadingStats((prev) => {
          const next = new Set(prev);
          next.delete(campaignUuid);
          return next;
        });
      }
    },
    [getCampaignStatistics, loadingStats],
  );

  useEffect(() => {
    if (campaigns.length > 0 && !campaignsLoading) {
      campaigns.forEach((campaign, index) => {
        if (campaign.uuid && !requestedStatsRef.current.has(campaign.uuid)) {
          setTimeout(() => {
            loadCampaignStatistics(campaign.uuid);
          }, index * 50);
        }
      });
    }
  }, [campaigns, campaignsLoading]);

  const showAuthor = currentUserType === "admin";

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search campaigns..."
        />
        <button
          type="button"
          onClick={handleCreate}
          className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
        >
          <Plus size={16} strokeWidth={2.5} />
          Create campaign
        </button>
      </div>

      <nav className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-(--border-subtle)">
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id] ?? 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 whitespace-nowrap border-0 bg-transparent px-4 py-[11px] text-[13.5px] transition-colors ${
                isActive
                  ? "font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:text-(--text-primary)"
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className="text-[12px] font-semibold text-(--text-muted)">
                  {count}
                </span>
              )}
              {isActive && (
                <span className="absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-(--brand-primary)" />
              )}
            </button>
          );
        })}
      </nav>

      {campaignsError && campaigns.length === 0 ? (
        <Card>
          <h3 className="text-base font-semibold text-(--text-primary)">
            Unable to load campaigns
          </h3>
          <p className="mt-1 text-sm text-(--text-secondary)">
            Please refresh or try again later.
          </p>
          <div className="mt-3">
            <Button variant="secondary" onClick={refreshCampaigns}>
              Retry
            </Button>
          </div>
        </Card>
      ) : campaignsLoading && campaigns.length === 0 ? (
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading campaigns…</p>
        </Card>
      ) : (
        <CampaignsTable
          campaigns={filtered}
          users={users}
          segments={segments}
          stats={campaignStats}
          loadingUuids={loadingStats}
          showAuthor={showAuthor}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyMessage={
            search
              ? "No campaigns match your search."
              : activeTab !== "all"
                ? `No ${activeTab} campaigns.`
                : "No campaigns yet. Create your first campaign to get started."
          }
        />
      )}
    </section>
  );
}
